import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileCode, Upload, Lock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { xmlUploadSchema } from '@/lib/validation';
import { parseTallyDate, parseFlexibleDate } from '@/lib/dateUtils';
import { errorLogger } from '@/lib/errorLogger';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TallyEntry {
  date: string;
  amount: number;
  narration: string;
  ledger: string;
  voucher_type?: string;
}

export function TallyImport() {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canAccess, nextTierPrice, tier } = usePlanLimits();
  const hasTallyIntegration = canAccess('hasTallyIntegration');

  const parseTallyXML = (xmlText: string): TallyEntry[] => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      const vouchers = Array.from(xmlDoc.getElementsByTagName('VOUCHER'));

      return vouchers.map(voucher => {
        const voucherType = voucher.getElementsByTagName('VOUCHERTYPENAME')[0]?.textContent || 'Payment';
        const dateText = voucher.getElementsByTagName('DATE')[0]?.textContent || '';
        
        // Use robust date parsing
        const date = dateText.length === 8 
          ? parseTallyDate(dateText)
          : parseFlexibleDate(dateText);

        const amountText = voucher.getElementsByTagName('AMOUNT')[0]?.textContent || '0';
        const amount = Math.abs(parseFloat(amountText));
        
        if (amount <= 0) return null;
        
        const narration = (voucher.getElementsByTagName('NARRATION')[0]?.textContent || 'Imported from Tally').trim().substring(0, 500);
        
        const ledgerEntries = Array.from(voucher.getElementsByTagName('LEDGERNAME'));
        const ledger = (ledgerEntries.length > 0 ? ledgerEntries[0].textContent || 'Unknown' : 'Unknown').substring(0, 200);

        return {
          date,
          amount,
          narration,
          ledger,
          voucher_type: voucherType
        } as TallyEntry;
      }).filter((entry): entry is TallyEntry => entry !== null);
    } catch (error) {
      console.error('XML parsing error:', error);
      throw new Error('Failed to parse Tally XML file');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    try {
      xmlUploadSchema.parse({ file });
      
      setImporting(true);
      setProgress(0);

      const text = await file.text();
      const entries = parseTallyXML(text);

      setProgress(30);

      if (entries.length === 0) {
        throw new Error('No valid entries found in XML file');
      }

      const batches = [];
      for (let i = 0; i < entries.length; i += 50) {
        batches.push(entries.slice(i, i + 50));
      }

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i].map(entry => ({
          ...entry,
          user_id: user.id,
          status: 'unmatched'
        }));

        const { error } = await supabase.from('tally_entries').insert(batch);
        
        if (error) throw error;

        setProgress(30 + ((i + 1) / batches.length) * 60);
      }

      await supabase.from('logs').insert({
        user_id: user.id,
        msg: `Imported ${entries.length} Tally entries from ${file.name}`,
        level: 'success',
        time: new Date().toLocaleTimeString()
      });

      setProgress(100);
      
      // Track successful import
      errorLogger.trackImport('tally', true, entries.length);
      
      toast({
        title: 'Import Successful',
        description: `Imported ${entries.length} entries from Tally XML`
      });

      e.target.value = '';
    } catch (error: any) {
      errorLogger.trackImport('tally', false, 0, error.message);
      toast({
        variant: 'destructive',
        title: 'Import Failed',
        description: error.message
      });
    } finally {
      setImporting(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  // Show upgrade prompt for non-Pro users
  if (!hasTallyIntegration) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <FileCode className="text-muted-foreground" size={24} />
          <div>
            <h3 className="font-bold text-foreground">Import from Tally</h3>
            <p className="text-sm text-muted-foreground">Upload Tally XML vouchers</p>
          </div>
        </div>
        <Alert className="border-primary/50 bg-primary/5">
          <Lock className="h-4 w-4 text-primary" />
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="flex-1">Tally integration is a Pro feature. Upgrade to import directly from Tally ERP.</span>
            <Button size="sm" onClick={() => navigate('/pricing')}>
              Upgrade to Pro - {nextTierPrice}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <FileCode className="text-primary" size={24} />
        <div>
          <h3 className="font-bold text-foreground">Import from Tally</h3>
          <p className="text-sm text-muted-foreground">Upload Tally XML vouchers</p>
        </div>
      </div>

      <label className="relative">
        <input
          type="file"
          accept=".xml,text/xml,application/xml"
          onChange={handleFileUpload}
          disabled={importing}
          className="hidden"
        />
        <Button
          variant="outline"
          disabled={importing}
          className="w-full"
          asChild
        >
          <span className="cursor-pointer">
            {importing ? (
              <>
                <Upload className="mr-2 h-4 w-4 animate-pulse" />
                Importing... {Math.round(progress)}%
              </>
            ) : (
              <>
                <FileCode className="mr-2 h-4 w-4" />
                Select Tally XML File
              </>
            )}
          </span>
        </Button>
      </label>

      {importing && (
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
